import React, { useState } from 'react';

/**
 * Foreign-order standoff banner (02 Aug 2026, simplified pass).
 *
 * By the time this banner fires, the durable-cloid persistence and the
 * boot-adoption pass have already handled every case where ownership
 * evidence exists. Anything reaching here is by definition "orders we
 * cannot prove are ours" — one honest wording covers it, and there is
 * exactly one action worth surfacing: cancel these specific orders and
 * resume. Dismissing the banner is not a separate action; it is just
 * leaving the bot paused, which is already the current state.
 *
 * Hard rule: the app NEVER auto-cancels orders the bot cannot positively
 * attribute to itself. A dual-instance scenario resolved by mutual
 * auto-cancel is the July order war. Standing aside and asking once is
 * the correct behaviour for the unprovable case.
 */
export function ForeignStandoffBanner({ alerts, onResolve }) {
  const [confirming, setConfirming] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const snapshot = Array.isArray(alerts.foreign_orders_snapshot)
    ? alerts.foreign_orders_snapshot
    : [];
  const count = snapshot.length;

  return (
    <div
      data-testid="foreign-orders-banner"
      style={{
        marginBottom: 10,
        padding: '12px 14px',
        borderRadius: 8,
        fontSize: 12.5,
        lineHeight: 1.5,
        background: 'rgba(248,113,113,0.16)',
        border: '1px solid rgba(248,113,113,0.65)',
        color: '#fca5a5',
      }}
    >
      <strong style={{ color: '#f87171' }}>
        {count} order{count === 1 ? '' : 's'} resting on this wallet cannot be verified as the bot&apos;s. Trading is paused.
      </strong>

      {count > 0 && (
        <div
          data-testid="foreign-orders-list"
          style={{
            marginTop: 10,
            padding: 8,
            borderRadius: 6,
            background: 'rgba(0,0,0,0.25)',
            fontFamily: 'monospace',
            fontSize: 11.5,
            color: '#e5e7eb',
          }}
        >
          {snapshot.slice(0, 12).map((o) => (
            <div key={o.oid} style={{ display: 'flex', gap: 12 }}>
              <span style={{ opacity: 0.7 }}>oid</span>
              <span>{o.oid}</span>
              <span style={{ opacity: 0.7 }}>•</span>
              <span>{o.side || '?'} {o.size}</span>
              <span style={{ opacity: 0.7 }}>@</span>
              <span>${Number(o.price).toLocaleString(undefined, { maximumFractionDigits: 6 })}</span>
            </div>
          ))}
          {snapshot.length > 12 && (
            <div style={{ opacity: 0.6, marginTop: 4 }}>… and {snapshot.length - 12} more</div>
          )}
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: 11.5, color: '#fda4a4' }}>
        Full history in the Bot Activity panel below.
      </div>

      {!confirming ? (
        <div style={{ marginTop: 12 }}>
          <button
            data-testid="standoff-cancel-and-resume-btn"
            onClick={() => setConfirming(true)}
            style={{
              padding: '7px 14px',
              borderRadius: 6,
              border: '1px solid rgba(248,113,113,0.7)',
              background: 'rgba(248,113,113,0.18)',
              color: '#fca5a5',
              cursor: 'pointer',
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            Cancel {count === 1 ? 'this order' : `these ${count} orders`} and resume
          </button>
        </div>
      ) : (
        <div
          data-testid="standoff-confirm-row"
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 6,
            background: 'rgba(0,0,0,0.35)',
            color: '#f3f4f6',
            fontSize: 12.5,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            Cancel {count === 1 ? 'this order' : `these ${count} orders`} on Hyperliquid and resume the bot?
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              data-testid="standoff-confirm-btn"
              disabled={inFlight}
              onClick={async () => {
                setInFlight(true);
                try {
                  await onResolve('cancel_and_resume');
                } finally {
                  setInFlight(false);
                  setConfirming(false);
                }
              }}
              style={{
                padding: '6px 12px',
                borderRadius: 5,
                border: '1px solid #34d399',
                background: 'rgba(52,211,153,0.16)',
                color: '#a7f3d0',
                cursor: inFlight ? 'wait' : 'pointer',
                fontSize: 12.5,
              }}
            >
              {inFlight ? 'Working…' : 'Confirm'}
            </button>
            <button
              data-testid="standoff-cancel-btn"
              disabled={inFlight}
              onClick={() => setConfirming(false)}
              style={{
                padding: '6px 12px',
                borderRadius: 5,
                border: '1px solid rgba(148,163,184,0.7)',
                background: 'transparent',
                color: '#cbd5e1',
                cursor: 'pointer',
                fontSize: 12.5,
              }}
            >
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
